import { useEffect, useState } from "react";
import api from "@/lib/api";
import FetesMap from "@/components/FetesMap";

// APLEO — live group beacon management (Fêtes 2027 → Gestion de projet → Balise groupe).
const T = {
  fr: {
    title: "Balise du groupe",
    intro: "Diffusez en direct la position du groupe (traversée de la baie, processions…). Elle apparaît en temps réel sur les cartes publiques — page Fêtes 2027 et agenda — sous forme de point rouge pulsant, rafraîchi toutes les 30 secondes.",
    statusOn: "Balise ACTIVE",
    statusOff: "Balise inactive",
    lastUpdate: "Dernière mise à jour",
    by: "par",
    useMyPos: "Utiliser ma position",
    locating: "Localisation…",
    geoError: "Position introuvable — vérifiez l'autorisation de géolocalisation.",
    lat: "Latitude",
    lng: "Longitude",
    label: "Message (optionnel)",
    labelPh: "Ex. : Traversée en cours — départ de la Bergerie",
    publish: "Publier la position",
    deactivate: "Désactiver la balise",
    saved: "✓ Position publiée",
    deactivated: "✓ Balise désactivée",
    error: "Erreur lors de l'enregistrement.",
    preview: "Aperçu de la carte publique",
  },
  en: {
    title: "Group beacon",
    intro: "Broadcast the group's live position (bay crossing, processions…). It appears in real time on the public maps — Feasts 2027 page and calendar — as a pulsing red dot, refreshed every 30 seconds.",
    statusOn: "Beacon ACTIVE",
    statusOff: "Beacon inactive",
    lastUpdate: "Last update",
    by: "by",
    useMyPos: "Use my position",
    locating: "Locating…",
    geoError: "Position unavailable — check geolocation permission.",
    lat: "Latitude",
    lng: "Longitude",
    label: "Message (optional)",
    labelPh: "E.g.: Crossing in progress — leaving the Bergerie",
    publish: "Publish position",
    deactivate: "Deactivate beacon",
    saved: "✓ Position published",
    deactivated: "✓ Beacon deactivated",
    error: "Error while saving.",
    preview: "Public map preview",
  },
};

export default function BeaconTab({ locale = "fr" }) {
  const t = T[locale] || T.fr;
  const [beacon, setBeacon] = useState(null);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [label, setLabel] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/presence/beacon");
      setBeacon(r.data);
      if (r.data?.active) {
        setLat(String(r.data.lat));
        setLng(String(r.data.lng));
        setLabel(r.data.label || "");
      }
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const useMyPosition = () => {
    if (!navigator.geolocation) { setMsg(t.geoError); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setLocating(false);
      },
      () => { setMsg(t.geoError); setLocating(false); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const save = async (active) => {
    setBusy(true);
    setMsg("");
    try {
      const body = active
        ? { lat: parseFloat(lat), lng: parseFloat(lng), label, active: true }
        : { lat: parseFloat(lat) || 48.6689, lng: parseFloat(lng) || -1.4431, label, active: false };
      const r = await api.put("/admin/presence/beacon", body);
      setBeacon(r.data.active ? r.data : { active: false });
      setMsg(active ? t.saved : t.deactivated);
      setMapKey((k) => k + 1);
    } catch {
      setMsg(t.error);
    }
    setBusy(false);
    setTimeout(() => setMsg(""), 3000);
  };

  const inputCls = "w-full border px-3 py-2 font-body text-sm";
  const inputStyle = { borderColor: "var(--border)", background: "#fff" };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="beacon-tab">
      <div>
        <h3 className="font-display text-2xl mb-2" style={{ color: "var(--ink)" }}>{t.title}</h3>
        <p className="font-body text-sm mb-6" style={{ color: "var(--muted)" }}>{t.intro}</p>

        <div className="flex items-center gap-3 mb-6" data-testid="beacon-status">
          <span className="inline-block w-3 h-3 rounded-full"
                style={{ background: beacon?.active ? "#c1272d" : "var(--border)", boxShadow: beacon?.active ? "0 0 8px rgba(193,39,45,0.7)" : "none" }} />
          <span className="font-caps text-[0.68rem]" style={{ color: beacon?.active ? "var(--cinnabar)" : "var(--muted)", letterSpacing: "0.18em" }}>
            {beacon?.active ? t.statusOn : t.statusOff}
          </span>
          {beacon?.active && beacon.updated_at && (
            <span className="font-body text-xs" style={{ color: "var(--muted)" }}>
              — {t.lastUpdate} {new Date(beacon.updated_at).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
              {beacon.updated_by ? ` ${t.by} ${beacon.updated_by}` : ""}
            </span>
          )}
        </div>

        <button onClick={useMyPosition} disabled={locating} data-testid="beacon-use-my-pos"
                className="btn btn-outline mb-5" style={{ borderColor: "var(--gold)", color: "var(--cinnabar)" }}>
          📍 {locating ? t.locating : t.useMyPos}
        </button>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="font-caps text-[0.6rem] block mb-1" style={{ color: "var(--muted)", letterSpacing: "0.16em" }}>{t.lat.toUpperCase()}</label>
            <input value={lat} onChange={(e) => setLat(e.target.value)} data-testid="beacon-lat"
                   className={inputCls} style={inputStyle} placeholder="48.6689" />
          </div>
          <div>
            <label className="font-caps text-[0.6rem] block mb-1" style={{ color: "var(--muted)", letterSpacing: "0.16em" }}>{t.lng.toUpperCase()}</label>
            <input value={lng} onChange={(e) => setLng(e.target.value)} data-testid="beacon-lng"
                   className={inputCls} style={inputStyle} placeholder="-1.4431" />
          </div>
        </div>
        <div className="mb-6">
          <label className="font-caps text-[0.6rem] block mb-1" style={{ color: "var(--muted)", letterSpacing: "0.16em" }}>{t.label.toUpperCase()}</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="beacon-label"
                 className={inputCls} style={inputStyle} placeholder={t.labelPh} maxLength={140} />
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={() => save(true)} disabled={busy || !lat || !lng} data-testid="beacon-publish"
                  className="btn btn-primary">
            {t.publish}
          </button>
          {beacon?.active && (
            <button onClick={() => save(false)} disabled={busy} data-testid="beacon-deactivate"
                    className="btn btn-outline" style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
              {t.deactivate}
            </button>
          )}
        </div>
        {msg && <p className="font-body text-sm mt-4" data-testid="beacon-msg" style={{ color: msg === t.error ? "var(--cinnabar)" : "var(--moss, #4a6741)" }}>{msg}</p>}
      </div>

      <div>
        <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
          ◇ {t.preview.toUpperCase()} ◇
        </div>
        <FetesMap key={mapKey} locale={locale} focus={beacon?.active ? { pos: [beacon.lat, beacon.lng], zoom: 13 } : null} />
      </div>
    </div>
  );
}
